import React, { useEffect, useRef, useState } from 'react';

import { AlertDialog, Button } from '@rbilabs/universal-components';

import { ErrorText } from 'components/checkout-errors-modal/checkout-error-cell/styled';
import { StyledHeader } from 'components/checkout-errors-modal/styled';
import { AlertDialogEventLogger } from 'components/dialog/alert-dialog-event-logger';
import { t } from 'state/intl/translations';

import {
  CartAnalyticsModalActionNames,
  CartAnalyticsModalNames,
  logCartModalAction,
} from '../../utils/analytics';

type CartPricingErrorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onTryAgain: () => void;
  onTryAnotherStore: () => void;
};

export const CartPricingErrorModal = ({
  isOpen,
  onClose,
  onTryAgain,
  onTryAnotherStore,
}: CartPricingErrorModalProps) => {
  const cancelRef = useRef(null);
  const [isVisible, setIsVisible] = useState(isOpen);
  const ignorePropOpenUntilClosedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      // parent has actually cleared the error -> allow opening again next time
      ignorePropOpenUntilClosedRef.current = false;
      setIsVisible(false);
      return;
    }

    // If the user just dismissed locally, ignore `isOpen=true` until parent clears it.
    if (!ignorePropOpenUntilClosedRef.current) {
      setIsVisible(true);
    }
  }, [isOpen]);

  if (!isVisible) {
    return null;
  }

  return (
    <AlertDialogEventLogger
      testID="cart-pricing-error-dialog"
      modalAppearanceEventMessage="Minimal cart pricing error"
      modalAppearanceHeader={t('unableToProcessOrder')}
      modalAppearanceMessage={t(
        'unableToProcessOrderAtThisTime',
        'We are unable to process your order at this time. Please try again later.'
      )}
      isErrorModal
      leastDestructiveRef={cancelRef}
      isOpen={isVisible}
      onClose={() => {
        ignorePropOpenUntilClosedRef.current = true;
        setIsVisible(false);
        onClose();
      }}
      closeOnOverlayClick={false}
    >
      <AlertDialog.Content>
        <AlertDialog.Header>
          <StyledHeader variant="headerTwo">{t('unableToProcessOrder')}</StyledHeader>
        </AlertDialog.Header>
        <AlertDialog.Body>
          <ErrorText>
            {t(
              'unableToProcessOrderAtThisTime',
              'We are unable to process your order at this time. Please try again later.'
            )}
          </ErrorText>
        </AlertDialog.Body>
        <AlertDialog.Footer>
          <Button.Group>
            <Button
              width="full"
              onPress={() => {
                logCartModalAction({
                  actionName: CartAnalyticsModalActionNames.TRY_AGAIN,
                  modalName: CartAnalyticsModalNames.PRICING_ERROR,
                });
                onTryAgain();
              }}
              testID="cart-pricing-error-try-again"
            >
              {t('tryAgain')}
            </Button>
            <Button
              width="full"
              variant="outline"
              onPress={() => {
                logCartModalAction({
                  actionName: CartAnalyticsModalActionNames.TRY_ANOTHER_STORE,
                  modalName: CartAnalyticsModalNames.PRICING_ERROR,
                });
                // Close immediately (portal), then navigate next tick.
                ignorePropOpenUntilClosedRef.current = true;
                setIsVisible(false);
                onClose();
                setTimeout(() => onTryAnotherStore(), 0);
              }}
              testID="cart-pricing-error-try-another-store"
            >
              {t('tryAnotherStore', 'Try another store')}
            </Button>
          </Button.Group>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialogEventLogger>
  );
};
